import { AfterViewChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { CalendarEvent } from 'angular-calendar';
import { Observable, of, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { IFiltrosAgenda } from 'src/app/pages/agenda/models/agenda';
import { AgendaService } from 'src/app/pages/agenda/services/agenda.service';
import { IListByFilterMedico, IMedico } from 'src/app/pages/doctors/models/doctor';
import { DoctorService } from 'src/app/pages/doctors/services/doctor.service';
import { EMessageType } from 'src/app/shared/data/response-default.interface';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { SessionService } from 'src/app/shared/services/session.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import {
  EConsultaTipo,
  EDuration,
  ESolicitacaoConsultaModalidade,
  ESolicitacaoConsultaStatus,
  IConsulta,
  ISolicitaoConsulta
} from '../../data/consultas';
import { ConsultasService } from '../../services/consultas.service';
import { EOrdenacao } from 'src/app/shared/data/ordenacao.enum';

@Component({
  selector: 'app-marcar-consulta',
  templateUrl: './marcar-consulta.component.html',
  styleUrls: ['./marcar-consulta.component.scss']
})
export class MarcarConsultaComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subject<any>();

  filtro = (d: Date | null): boolean => {
    const day = (d || new Date()).getDay();
    // Prevent Saturday and Sunday from being selected.
    return day !== 0 && day !== 6;
  };

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view) => {
    return '';
  };

  formAgendarConsulta = new UntypedFormGroup({
    dia: new UntypedFormControl('', [Validators.required]),
    horario: new UntypedFormControl('', [Validators.required]),
    duration: new UntypedFormControl(EDuration.QuinzeMin, [Validators.required]),
    doctorId: new UntypedFormControl('', [Validators.required])
  });

  solicitacaoConsultaId!: string;
  dadosSolicitacaoConsulta!: ISolicitaoConsulta;

  doctors: IMedico[] = [];
  isDoctor = true;

  minDate = new Date();
  scheduleDate?: Date;
  viewDate?: Date;

  events: CalendarEvent[] = [];
  locale: string = 'pt-BR';

  duration = [
    {
      value: EDuration.QuinzeMin,
      label: '15 Min'
    },
    {
      value: EDuration.TrintaMIn,
      label: '30 Min'
    }
  ];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly agendaService: AgendaService,
    private readonly consultasService: ConsultasService,
    private readonly doctorService: DoctorService,
    private readonly toastService: ToastService,
    private readonly sessionService: SessionService,
    private readonly router: Router
  ) { }

  ngOnInit(): void {
    this.solicitacaoConsultaId = this.activatedRoute.snapshot.paramMap.get('id')!;
    this.isDoctor = this.sessionService.getSession().role === ERoleAdminString.Doctor;

    this.carregarMedicos();
    this.carregarDadosSolicitacaoConsulta();
    this.carregarAgendamentos();

    this.durationControl.valueChanges.pipe(takeUntil(this.subscriptions)).subscribe((duration) => {
      this.editScheduleEvent(+duration);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.next(null);
    this.subscriptions.complete();
  }

  carregarDadosSolicitacaoConsulta(): void {
    this.consultasService.getDadosSolicitacaoConsulta(this.solicitacaoConsultaId).subscribe((resposta) => {
      this.dadosSolicitacaoConsulta = resposta.data;

      this.selecionarDiaMaisProximoAgendavel();
      this.aplicarFiltroDias();
    });
  }

  agendarConsulta(): void {
    if (!this.scheduleDate) {
      this.toastService.toast('É necessário selecionar um horário.', EMessageType.Warning);
      return;
    }

    this.horarioControl.patchValue(this.scheduleDate.getHours() + ':' + this.scheduleDate.getMinutes());

    if (this.formAgendarConsulta.invalid) {
      this.toastService.toast('Todos os campos são obrigatórios e devem ser preenchidos.', EMessageType.Warning);
      return;
    }

    if (!this.checkDataValida()) {
      this.toastService.toast('A data selecionada não condiz com o que o colaborador solicitou.', EMessageType.Warning);
      return;
    }

    if (
      !this.agendaService.checkHorarioValido(this.horarioControl.value, this.dadosSolicitacaoConsulta.preferredTimes)
    ) {
      this.toastService.toast('A hora selecionada não condiz com o que o colaborador solicitou.', EMessageType.Warning);
      return;
    }

    const dadosForm = this.formAgendarConsulta.getRawValue();

    const date = new Date(dadosForm.dia);
    date.setHours(dadosForm.horario.split(':')[0]);
    date.setMinutes(dadosForm.horario.split(':')[1]);
    const appointmentDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);

    const dados: IConsulta = {
      appointmentDate: appointmentDate.toISOString(),
      employeeId: this.dadosSolicitacaoConsulta!.employeeId,
      modality: this.dadosSolicitacaoConsulta!.modality,
      duration: dadosForm.duration,
      doctorId: this.doctorControl.value,
      type: EConsultaTipo.Rotina,
      medicalAppointmentRequestId: this.dadosSolicitacaoConsulta?.id
    };

    this.consultasService.criarConsulta(dados).subscribe((res) => {
      if (res?.messages) {
        this.toastService.toast(res?.messages);
      }

      if (res.isSucceed) {
        this.router.navigateByUrl('/dash/consultas');
      }
    });
  }

  selectTime(event: { date: Date }): void {
    if (this.scheduleDate) {
      this.events.pop();
    }

    this.scheduleDate = event.date;
    const startDate = new Date(event.date);

    this.events = [...this.events, this.createScheduleEvent(startDate)];
  }

  private checkDataValida(): boolean {
    const dataSelecionada = this.formAgendarConsulta.get('dia')?.value;

    // se data selecionada for nula retorna falso
    if (!dataSelecionada) {
      return false;
    }

    // se data selecionada for antes de hoje retorna falso
    if (new Date(dataSelecionada) < new Date()) {
      return false;
    }

    // se data selecionada não for no dia que o paciente solicitou retorna falso
    if (!this.checkDiaSemanaSelecionado(dataSelecionada)) {
      return false;
    }

    return true;
  }

  private checkDiaSemanaSelecionado(dataSelecionada: string): boolean {
    const diaSemanaSelecionado = this.agendaService.getNomeDoDiaByData(new Date(dataSelecionada));

    const diasSelecionadosColaborador = this.dadosSolicitacaoConsulta?.preferredDays.split(',');

    // se o dia da semana selecionado pelo Atendente estiver entre os dias selecionados pelo usuário retorna true, caso contrário retorna false
    return !!diasSelecionadosColaborador?.find((diaSemana) => diaSemanaSelecionado === diaSemana);
  }

  private carregarAgendamentos(): void {
    this.diaControl.valueChanges
      .pipe(
        takeUntil(this.subscriptions),
        switchMap((dia) => {
          const diaSelecionado = new Date(dia);

          if (!diaSelecionado.getTime()) {
            return of([]);
          }

          this.viewDate = new Date(diaSelecionado.getFullYear(), diaSelecionado.getMonth(), diaSelecionado.getDate());

          return this.getEventosAgendaMedico(diaSelecionado);
        })
      )
      .subscribe((events) => (this.events = events));
  }

  private getEventosAgendaMedico(data: Date): Observable<CalendarEvent[]> {
    const filtros = this.getFiltrosAgendaMedico(data);

    return this.agendaService.carregarAgendaMedico(filtros).pipe(
      takeUntil(this.subscriptions),
      map((res) => {
        const events: CalendarEvent[] = res.data.map((consulta: IConsulta) => {
          const horaComecoConsulta = new Date(consulta.appointmentDate);
          return this.agendaService.createScheduleEvent(
            horaComecoConsulta,
            consulta.duration,
            consulta.employee?.name!,
            consulta.status!,
            consulta.modality,
            consulta.id
          );
        });

        if (this.scheduleDate && this.viewDate) {
          events.push(
            this.createScheduleEvent(
              new Date(
                this.viewDate.getFullYear(),
                this.viewDate.getMonth(),
                this.viewDate.getDate(),
                this.scheduleDate.getHours(),
                this.scheduleDate.getMinutes()
              )
            )
          );
        }

        return events;
      })
    );
  }

  private getFiltrosAgendaMedico(data: Date): IFiltrosAgenda {
    const filtros: IFiltrosAgenda = {
      doctorId: this.doctorControl.value,
      periodStart: new Date(data.getFullYear(), data.getMonth(), data.getDate()).toISOString(),
      periodEnd: new Date(data.getFullYear(), data.getMonth(), data.getDate() + 1).toISOString()
    };

    const partnerId = this.sessionService.getSession().partnerId;
    const companyId = this.sessionService.getSession().companyId;
    const accountRole = this.sessionService.getSession().accountRole;

    if (partnerId) {
      filtros.partnerId = partnerId;
    }

    if (companyId) {
      filtros.companyId = companyId;
    }

    if (accountRole) {
      filtros.accountRole = parseInt(accountRole);
    }

    return filtros;
  }

  private aplicarFiltroDias(): void {
    const dias: number[] = this.getNumerosDiasPreferidos();

    this.filtro = (d: Date | null): boolean => {
      const day = (d || new Date()).getDay();

      return dias.includes(day);
    };

    this.dateClass = (cellDate, view): string => {
      // Only highligh dates inside the month view.
      if (view === 'month') {
        return this.minDate < cellDate && dias.includes(cellDate.getDay()) ? 'calendario-dia-desejavel' : '';
      }

      return '';
    };
  }

  private selecionarDiaMaisProximoAgendavel(): void {
    const dataAtual = new Date();
    const data = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), dataAtual.getDate() + 1);

    const diasPreferidos = this.getNumerosDiasPreferidos();

    if (diasPreferidos.length === 0) {
      return;
    }

    while (!diasPreferidos.includes(data.getDay())) {
      data.setDate(data.getDate() + 1);
    }

    this.diaControl.patchValue(data);
  }

  private getDiaNumberoByNome(dataSelecionada: string): number {
    switch (dataSelecionada) {
      case 'segunda':
        return 1;
      case 'terca':
        return 2;
      case 'quarta':
        return 3;
      case 'quinta':
        return 4;
      case 'sexta':
        return 5;
      case 'sabado':
        return 6;
      default:
        return 0;
    }
  }

  private getNumerosDiasPreferidos(): number[] {
    const dias: number[] = [];
    if (this.dadosSolicitacaoConsulta) {
      this.dadosSolicitacaoConsulta.preferredDays.split(',').forEach((dia) => {
        dias.push(this.getDiaNumberoByNome(dia));
      });
    }

    return dias;
  }

  private get diaControl(): UntypedFormControl {
    return this.formAgendarConsulta.get('dia') as UntypedFormControl;
  }

  private get horarioControl(): UntypedFormControl {
    return this.formAgendarConsulta.get('horario') as UntypedFormControl;
  }

  private get doctorControl(): UntypedFormControl {
    return this.formAgendarConsulta.get('doctorId') as UntypedFormControl;
  }

  private get durationControl(): UntypedFormControl {
    return this.formAgendarConsulta.get('duration') as UntypedFormControl;
  }

  private carregarMedicos(): void {
    if (!this.isDoctor) {
      const partnerId = this.sessionService.getSession().partnerId;
      const accountRole = this.sessionService.getSession().accountRole;
      const companyId = this.sessionService.getSession().companyId;

      const filtros: IListByFilterMedico = {
        pageNumber: 0,
        pageSize: 9999,
        orderBy: EOrdenacao.AlfabeticaAZ
      };

      if (accountRole) {
        filtros.accountRole = parseInt(accountRole);
      }

      if (partnerId) {
        filtros.partnerId = partnerId;
      }

      if (companyId) {
        filtros.companyId = companyId;
      }

      this.doctorService
        .listarTodosMedicos(filtros)
        .pipe(take(1))
        .subscribe((res) => {
          if (res.isSucceed) {
            this.doctors = res.data;
            this.doctorControl.patchValue(res.data[0]?.id);
          }
        });
    } else {
      this.doctorControl.patchValue(this.sessionService.getSession().sid, { emitEvent: false });
    }

    this.doctorControl.valueChanges
      .pipe(switchMap(() => this.getEventosAgendaMedico(this.viewDate ?? new Date())))
      .subscribe((events) => (this.events = events));
  }

  private editScheduleEvent(duration: number): void {
    if (this.scheduleDate) {
      this.events = [
        ...this.events.map((event, index) => {
          if (index === this.events.length - 1) {
            event.end = this.agendaService.getEndScheduleDate(event.start, +duration);
          }

          return event;
        })
      ];
    }
  }

  private createScheduleEvent(date: Date) {
    return this.agendaService.createScheduleEvent(
      date,
      +(this.durationControl.value ?? 15),
      'NOVA CONSULTA',
      ESolicitacaoConsultaStatus.pendenteAprovacao,
      this.dadosSolicitacaoConsulta?.modality
    );
  }
}
